import { useSelector } from "react-redux";

import { IconButton } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { COMPANY_ANALYSIS_SIGNALS } from "app/pages/sales-intelligence/constants/routes";
import useSignalsEventsQuery from "app/pages/sales-intelligence/modules/company-analysis/queries/useSignalsEventsQuery";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import SignalEventBoxItem from "app/pages/sales-intelligence/pages/company-analysis/components/cards/signals-card/SignalEventBoxItem/SignalEventBoxItem";
import SignalsCardEmpty from "app/pages/sales-intelligence/pages/company-analysis/components/cards/signals-card/SignalsCardEmpty/SignalsCardEmpty";
import Card from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/Card";
import CardFooter from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardFooter/CardFooter";
import CardHeader from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Card/CardHeader/CardHeader";
import LoaderGradient from "app/pages/sales-intelligence/pages/company-analysis/components/shared/Loader/LoaderGradient";
import { COMPANY_ANALYSIS_TAB_NAMES } from "app/pages/sales-intelligence/pages/company-analysis/constants";
import type { TrackingProps } from "app/pages/sales-intelligence/pages/company-analysis/containers/signals/SignalEventList/withSignalEventsTracking";
import { selectSignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import GrandAccessBanner from "app/pages/sales-intelligence/common-components/GrandAccessBanner/GrandAccessBanner";
import { useCompanyName } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCompanyName";
import {
    ICON_IMAGE_PATH,
    SIGNALS_LIMIT,
    SIGNALS_LIMIT_WITH_INTENT_UPSELL,
} from "app/pages/sales-intelligence/pages/company-analysis/containers/overview/SignalsCard/constants";
import intentTrackingService from "app/pages/sales-intelligence/services/tracking/intent/trackingService";
import { showIntentUpsellTopicHook } from "app/pages/sales-intelligence/sub-modules/intent/internalHooks/hooks";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

const SignalsCard = ({ onArticleLinkClick, onCompanyLinkClick }: TrackingProps) => {
    const { data, isLoading, isFetched } = useSignalsEventsQuery();
    const isEmpty = isFetched && (!data || data.length === 0);

    const companyName = useCompanyName();

    const isShowIntentUpsellBanner = !salesSettingsHelper.isIntentSignalsEnabled();

    const signalLimit = isShowIntentUpsellBanner ? SIGNALS_LIMIT_WITH_INTENT_UPSELL : SIGNALS_LIMIT;

    const signalsDescription = useSelector(selectSignalsDescription);

    const onClickGrandAccess = () => {
        void intentTrackingService.trackIntentHook("intent hook", "open popup");
        showIntentUpsellTopicHook();
    };

    const renderContent = () => {
        if (isEmpty && !isShowIntentUpsellBanner) {
            return <SignalsCardEmpty />;
        }

        if (isLoading) {
            return (
                <Box padding="8px">
                    <LoaderGradient height={344} />
                </Box>
            );
        }

        return (
            <Box minHeight="364px">
                <Flex
                    justifyContent="flex-start"
                    flexWrap="wrap"
                    padding="8px 0 8px 8px"
                    gap="8px"
                >
                    {data?.slice(0, signalLimit).map((signal, index) => (
                        <SignalEventBoxItem
                            key={index}
                            signal={signal}
                            signalsDescription={signalsDescription}
                            onArticleLinkClick={onArticleLinkClick}
                            onCompanyLinkClick={onCompanyLinkClick}
                        />
                    ))}
                    {isShowIntentUpsellBanner ? (
                        <Flex
                            data-automation="signal-event-item-grand-access"
                            data-automation-type="signal-event-item-grand-access"
                            flexGrow={1}
                            borderRadius="6px"
                            width="calc(50% - 8px)"
                            maxWidth="calc(50% - 8px)"
                            minHeight="168px"
                        >
                            <GrandAccessBanner
                                title={translate("si.signals.intent-upgrade.banner.title")}
                                description={translate("si.signals.intent-upgrade.banner.description")}
                                iconImagePath={ICON_IMAGE_PATH}
                                button={
                                    <IconButton
                                        type={"trial"}
                                        onClick={onClickGrandAccess}
                                        iconName={"sparkles"}
                                    >
                                        {translate("si.common.upgrade")}
                                    </IconButton>
                                }
                            />
                        </Flex>
                    ) : null}
                </Flex>
            </Box>
        );
    };

    return (
        <Card
            dataAutomation="signals-card"
            header={<CardHeader title={translate("si.company-analysis.signals.card.header")} />}
            footer={
                <CardFooter
                    stateId={COMPANY_ANALYSIS_SIGNALS}
                    linkText="si.company-analysis.signals.card.full_report"
                    isLoading={isLoading}
                    onClick={() => {
                        companyAnalysisTrackingService.widgets.trackWidgetFooterButtonClick(
                            companyName,
                            COMPANY_ANALYSIS_TAB_NAMES.signals,
                        );
                    }}
                />
            }
            renderContent={renderContent}
            width="50%"
        />
    );
};

export default SignalsCard;
